import { api, fetchServer } from "@/features/utils/fetch-server";
import type { Document } from "../types";

export class DocumentsAPI {
  // Upload one or more documents
  static uploadDocuments = async (
    clientId: string,
    files: File[],
  ): Promise<Document[]> => {
    const results: Document[] = [];

    // Upload files sequentially to avoid overwhelming the server
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      // FormData must not be JSON-serialized, so this one stays on fetchServer.
      const response = await fetchServer(`/api/rag/v1/${clientId}/documents`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      results.push(data.document);
    }

    return results;
  };

  // Get document by ID
  static getDocument = async (
    clientId: string,
    documentId: string,
  ): Promise<Document> => {
    const data = await api.get<{ document: Document }>(
      `/api/rag/v1/${clientId}/documents/${documentId}`,
    );
    return data.document;
  };

  // List documents
  static listDocuments = async (
    clientId: string,
    options?: {
      status?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<{ documents: Document[]; total: number }> => {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.offset) params.append("offset", options.offset.toString());

    const queryString = params.toString() ? `?${params}` : "";
    return api.get(`/api/rag/v1/${clientId}/documents${queryString}`);
  };

  // Delete a document
  static deleteDocument = async (
    clientId: string,
    documentId: string,
  ): Promise<{ success: boolean }> =>
    api.delete(`/api/rag/v1/${clientId}/documents/${documentId}`);

  // Re-embed a document
  static reEmbedDocument = async (
    clientId: string,
    documentId: string,
  ): Promise<{ success: boolean; message: string }> =>
    api.post(`/api/rag/v1/${clientId}/documents/${documentId}/re-embed`);

  // Search repository
  static searchRepository = async (
    clientId: string,
    query: string,
    options?: {
      limit?: number;
      threshold?: number;
    },
  ): Promise<{
    results: Array<{
      chunk: {
        id: string;
        content: string;
        document_id: string;
        chunk_index: number;
        similarity: number;
        metadata: Record<string, unknown>;
      };
      document: {
        id: string;
        filename: string;
      };
    }>;
  }> =>
    api.post(`/api/rag/v1/${clientId}/search`, {
      query,
      limit: options?.limit || 5,
      threshold: options?.threshold,
    });
}
